import {
  COPY_METADATA_SOURCE,
  COPY_METADATA_TARGET,
} from '@disco/constants/ItemTypes'
import { pick } from '@disco/utils/array'
import { getTrackMetadataFieldName } from '@disco/utils/track-metadata'
import cn from 'clsx'
import uniqBy from 'lodash/uniqBy'
import without from 'lodash/without'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'hooks/use-redux'
import type { CopyMetadataFields } from 'types/copy-metadata'
import type { Discussion } from 'types/discussion'
import type { RightsSplitChain } from 'types/rights-data'
import type { FullWriterSplit, NativeTag, Track, TrackId } from 'types/track'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { getVisibleCustomFieldsKeys } from '@disco/state-management/selectors/user'
import { newlines } from 'utils/dom'
import CopyMetadataArtwork from 'components/common/copy-metadata-artwork'
import CopyMetadataField from 'components/common/copy-metadata-field'
import TrackInfo from 'components/common/track-info'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Draggable from 'components/ui/draggable'
import ExpandableList from 'components/ui/expandable-list'
import Flex from 'components/ui/flex'
import { HoverProvider } from 'components/ui/hover-provider'
import TreeItem from 'components/ui/tree-item'
import LoadingIndicator from './loading-indicator'
import styles from './index.module.css'
import { collectNoteIds, getNotePreview, getRightsSplitText } from './utils'

const METADATA_FIELDS_KEYS = [
  'Title',
  'Artist',
  'Album',
  'Composer',
  'Grouping',
  'Genre',
  'Year',
  'Release Date',
  'BPM',
  'ISRC',
  'TrackNumber',
  'Comment',
]

const insertAfter = (
  items: string[],
  target: string,
  value: string,
): string[] => items.flatMap(item => (item === target ? [item, value] : [item]))

type SourceDiscussions = {
  internal: Discussion[]
  client: Discussion[]
}

type SourceRightsSplits = {
  publishing: RightsSplitChain[]
  recording: RightsSplitChain[]
}

type Props = {
  disabled?: boolean
  expanded?: boolean
  loading: boolean
  overrides?: Omit<Partial<Track>, 'track_writers'> & {
    track_writers?: FullWriterSplit[]
  }
  recordingTabEnabled?: boolean
  selectable?: boolean
  selectedFields: CopyMetadataFields
  sourceDiscussions?: SourceDiscussions
  sourceRightsSplits?: SourceRightsSplits
  trackId: TrackId
  onRemoveClick: (id: TrackId) => void
  onSelect?: (
    arg0:
      | ((arg0: CopyMetadataFields) => CopyMetadataFields)
      | CopyMetadataFields,
  ) => void
}

/**
 * Comprehensive track metadata display and selection component for copy metadata interface
 * Shows expandable track information with selectable metadata fields, artwork, tags, rights, and comments
 * Supports both source (selectable) and target (display) modes with visual diff indicators
 * @param disabled - Whether the track component is disabled
 * @param expanded - Whether the metadata details are expanded by default
 * @param loading - Whether the track is in loading state
 * @param overrides - Override values to display with diff styling
 * @param selectable - Whether this is a source track for selection
 * @param trackId - ID of the track to display
 * @param selectedFields - Currently selected metadata fields
 * @param onRemoveClick - Callback when remove button is clicked
 * @param onSelect - Callback when field selection changes
 */
const CopyMetadataTrack = ({
  disabled,
  expanded = false,
  loading,
  overrides,
  recordingTabEnabled,
  selectable,
  sourceDiscussions,
  sourceRightsSplits,
  trackId,
  selectedFields = {} as CopyMetadataFields,
  onRemoveClick,
  onSelect,
}: Props) => {
  const {
    artwork,
    note_ids,
    metadata_keys,
    native_tags_indexes,
    rights_split_ids,
  } = selectedFields
  const customFieldsKeys: string[] = useSelector(getVisibleCustomFieldsKeys)
  const track: Track | null | undefined = useSelector(state => {
    return getTrack(state, {
      id: trackId,
    })
  })
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  useEffect(() => {
    setIsExpanded(expanded)
  }, [expanded])
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'Metadata',
  ])

  const getVisibleMetadata = (field: string) =>
    (overrides?.metadata && overrides?.metadata[field]) ??
    track?.metadata[field] ??
    ''

  const fieldKeys = recordingTabEnabled
    ? insertAfter(METADATA_FIELDS_KEYS, 'Genre', 'Copyright')
    : METADATA_FIELDS_KEYS
  const visibleMetadata: string[] = fieldKeys.filter(getVisibleMetadata)
  const metadata: string[] = selectable ? fieldKeys : visibleMetadata
  const lyrics: string = getVisibleMetadata('Lyrics')
  const publishingChains: RightsSplitChain[] =
    sourceRightsSplits?.publishing ?? []
  const recordingChains: RightsSplitChain[] =
    sourceRightsSplits?.recording ?? []
  const publishingChainIds: string[] = publishingChains.map(c => String(c.id))
  const recordingChainIds: string[] = recordingChains.map(c => String(c.id))
  const allRightsChainIds: string[] = [
    ...publishingChainIds,
    ...recordingChainIds,
  ]
  const tags: NativeTag[] = uniqBy(
    [...(track?.native_tags ?? []), ...(overrides?.native_tags ?? [])],
    'id',
  )
  const internalSourceDiscussions: Discussion[] =
    sourceDiscussions?.internal ?? []
  const clientSourceDiscussions: Discussion[] = sourceDiscussions?.client ?? []
  const visibleCustomFields: string[] =
    customFieldsKeys.filter(getVisibleMetadata)
  const customFields: string[] = selectable
    ? customFieldsKeys
    : visibleCustomFields
  const handleExpand = useCallback(() => {
    setIsExpanded(state => !state)
  }, [])
  const handleRemove = useCallback(() => {
    onRemoveClick(trackId)
  }, [onRemoveClick, trackId])
  const handleSelect = useCallback(
    (data: Partial<CopyMetadataFields>) => {
      if (onSelect) {
        onSelect((state: CopyMetadataFields) => {
          const update = Object.keys(data).reduce<CopyMetadataFields>(
            (accumulator: CopyMetadataFields, key: string) => ({
              ...accumulator,
              [key]:
                typeof data[key] === 'object'
                  ? { ...state[key], ...data[key] }
                  : data[key],
            }),
            {} as CopyMetadataFields,
          )
          return { ...state, ...update }
        })
      }
    },
    [onSelect],
  )
  const toggleSection = useCallback((key: string) => {
    setExpandedSections((state: string[]) =>
      state.includes(key) ? without(state, key) : [...state, key],
    )
  }, [])
  if (!track) return null

  const renderDiscussionItem = (discussion: Discussion) => {
    const text = getNotePreview(discussion.body_raw)
    return selectable ? <>{text}</> : <ins>{text}</ins>
  }

  const renderRightsChainItem = (chain: RightsSplitChain) => {
    const text = getRightsSplitText(chain)
    return selectable ? <>{text}</> : <ins>{text}</ins>
  }

  const renderRightsSubsection = (
    label: 'Publishing' | 'Recording',
    chains: RightsSplitChain[],
    chainIds: string[],
  ) => {
    const sectionExpanded = expandedSections.includes(label)
    if (!selectable) {
      const chainsToCopy = chains.filter(c => rights_split_ids?.[String(c.id)])
      if (chainsToCopy.length === 0) {
        return null
      }
      return (
        <React.Fragment key={label}>
          <CopyMetadataField
            caption={label}
            depth={1}
            expanded={sectionExpanded}
            id={label}
            onExpand={toggleSection}
          />
          {sectionExpanded &&
            chainsToCopy.map(chain => (
              <CopyMetadataField
                key={chain.id}
                caption={renderRightsChainItem(chain)}
                depth={2}
              />
            ))}
        </React.Fragment>
      )
    }

    if (chains.length === 0) {
      return null
    }

    return (
      <React.Fragment key={label}>
        <CopyMetadataField
          caption={label}
          data={{ rights_split_ids: pick(rights_split_ids, chainIds) }}
          depth={1}
          expanded={sectionExpanded}
          id={label}
          onExpand={toggleSection}
          onSelect={onSelect ? handleSelect : undefined}
        />
        {sectionExpanded &&
          chains.map(chain => (
            <CopyMetadataField
              key={chain.id}
              caption={renderRightsChainItem(chain)}
              data={{
                rights_split_ids: pick(rights_split_ids, [String(chain.id)]),
              }}
              depth={2}
              onSelect={onSelect ? handleSelect : undefined}
            />
          ))}
      </React.Fragment>
    )
  }

  const renderDiscussionsSection = (
    label: string,
    discussionItems: Discussion[],
    baseDepth: number = 0,
  ) => {
    if (!selectable) {
      // display non selectable preview comment for target track

      const commentsToCopy = discussionItems.filter(
        d => note_ids?.[String(d.id)],
      )
      if (commentsToCopy.length === 0) {
        return null
      }

      const sectionExpanded = expandedSections.includes(label)
      return (
        <React.Fragment key={label}>
          <CopyMetadataField
            caption={label}
            depth={baseDepth}
            expanded={sectionExpanded}
            id={label}
            onExpand={toggleSection}
          />
          {sectionExpanded &&
            commentsToCopy.map(d => {
              const repliesToCopy = (d.replies ?? []).filter(
                r => note_ids?.[String(r.id)],
              )
              const hasReplies = repliesToCopy.length > 0
              const replyKey = `${label}-replies-${d.id}`
              const repliesExpanded = expandedSections.includes(replyKey)
              return (
                <React.Fragment key={d.id}>
                  <CopyMetadataField
                    caption={renderDiscussionItem(d)}
                    depth={baseDepth + 1}
                    expanded={hasReplies ? repliesExpanded : undefined}
                    id={hasReplies ? replyKey : undefined}
                    onExpand={hasReplies ? toggleSection : undefined}
                  />
                  {hasReplies &&
                    repliesExpanded &&
                    repliesToCopy.map(reply => (
                      <CopyMetadataField
                        key={reply.id}
                        caption={renderDiscussionItem(reply)}
                        depth={baseDepth + 2}
                      />
                    ))}
                </React.Fragment>
              )
            })}
        </React.Fragment>
      )
    }
    if (discussionItems.length === 0) {
      return null
    }

    const sectionIds = Object.keys(collectNoteIds(discussionItems))
    const sectionExpanded = expandedSections.includes(label)
    return (
      <React.Fragment key={label}>
        <CopyMetadataField
          caption={label}
          data={{ note_ids: pick(note_ids, sectionIds) }}
          depth={baseDepth}
          expanded={sectionExpanded}
          id={label}
          onExpand={toggleSection}
          onSelect={onSelect ? handleSelect : undefined}
        />
        {sectionExpanded &&
          discussionItems.map(d => {
            const replyKey = `${label}-replies-${d.id}`
            const replyIds = (d.replies ?? []).map(r => String(r.id))
            const hasReplies = replyIds.length > 0
            const repliesExpanded = expandedSections.includes(replyKey)
            return (
              <React.Fragment key={d.id}>
                <CopyMetadataField
                  caption={renderDiscussionItem(d)}
                  data={{
                    note_ids: pick(note_ids, [String(d.id), ...replyIds]),
                  }}
                  depth={baseDepth + 1}
                  expanded={hasReplies ? repliesExpanded : undefined}
                  id={hasReplies ? replyKey : undefined}
                  onExpand={hasReplies ? toggleSection : undefined}
                  onSelect={onSelect ? handleSelect : undefined}
                />
                {hasReplies &&
                  repliesExpanded &&
                  d.replies!.map(reply => (
                    <CopyMetadataField
                      key={reply.id}
                      caption={renderDiscussionItem(reply)}
                      data={{ note_ids: pick(note_ids, [String(reply.id)]) }}
                      depth={baseDepth + 2}
                      onSelect={onSelect ? handleSelect : undefined}
                    />
                  ))}
              </React.Fragment>
            )
          })}
      </React.Fragment>
    )
  }

  const renderNotesSection = () => {
    const sectionExpanded = expandedSections.includes('Notes')

    if (!selectable) {
      const hasInternalNotes = internalSourceDiscussions.some(
        d =>
          note_ids?.[String(d.id)] ||
          (d.replies ?? []).some(r => note_ids?.[String(r.id)]),
      )
      const hasClientNotes = clientSourceDiscussions.some(
        d =>
          note_ids?.[String(d.id)] ||
          (d.replies ?? []).some(r => note_ids?.[String(r.id)]),
      )
      if (!hasInternalNotes && !hasClientNotes) {
        return null
      }
      return (
        <>
          <CopyMetadataField
            caption="Notes"
            depth={0}
            expanded={sectionExpanded}
            id="Notes"
            onExpand={toggleSection}
          />
          {sectionExpanded && (
            <>
              {renderDiscussionsSection(
                'Notes (Internal)',
                internalSourceDiscussions,
                1,
              )}
              {renderDiscussionsSection(
                'Notes (Client)',
                clientSourceDiscussions,
                1,
              )}
            </>
          )}
        </>
      )
    }

    if (
      internalSourceDiscussions.length === 0 &&
      clientSourceDiscussions.length === 0
    ) {
      return null
    }

    const allNoteIds = [
      ...Object.keys(collectNoteIds(internalSourceDiscussions)),
      ...Object.keys(collectNoteIds(clientSourceDiscussions)),
    ]

    return (
      <>
        <CopyMetadataField
          caption="Notes"
          data={{ note_ids: pick(note_ids, allNoteIds) }}
          depth={0}
          expanded={sectionExpanded}
          id="Notes"
          onExpand={toggleSection}
          onSelect={onSelect ? handleSelect : undefined}
        />
        {sectionExpanded && (
          <>
            {renderDiscussionsSection(
              'Notes (Internal)',
              internalSourceDiscussions,
              1,
            )}
            {renderDiscussionsSection(
              'Notes (Client)',
              clientSourceDiscussions,
              1,
            )}
          </>
        )}
      </>
    )
  }

  const renderMetadata = () => (
    <div className={styles.copyMetadataTrackMetadata}>
      <CopyMetadataField
        caption="Metadata"
        data={{
          artwork,
          metadata_keys: pick(metadata_keys, fieldKeys),
        }}
        depth={0}
        expanded={expandedSections.includes('Metadata')}
        id="Metadata"
        onExpand={toggleSection}
        onSelect={onSelect ? handleSelect : undefined}
      />
      {expandedSections.includes('Metadata') && (
        <ExpandableList
          limit={5}
          renderButton={button => <TreeItem caption={button} depth={1} />}
        >
          {(selectable || overrides?.artwork || track.artwork) && (
            <CopyMetadataField
              caption={renderArtworkField()}
              data={{
                artwork,
              }}
              depth={1}
              disabled={!track.artwork && !overrides?.artwork}
              onSelect={onSelect ? handleSelect : undefined}
            />
          )}
          {metadata.map((field: string) => (
            <CopyMetadataField
              key={field}
              caption={renderMetadataField(field)}
              data={{
                metadata_keys: pick(metadata_keys, [field]),
              }}
              depth={1}
              disabled={
                selectable ? !track.metadata[field] : !metadata.includes(field)
              }
              onSelect={onSelect ? handleSelect : undefined}
            />
          ))}
        </ExpandableList>
      )}

      {(selectable || lyrics) && (
        <>
          <CopyMetadataField
            caption="Lyrics"
            depth={0}
            disabled={!lyrics}
            expanded={expandedSections.includes('Lyrics')}
            data={{
              metadata_keys: pick(metadata_keys, ['Lyrics']),
            }}
            id="Lyrics"
            onExpand={toggleSection}
            onSelect={onSelect ? handleSelect : undefined}
          />
          {expandedSections.includes('Lyrics') && (
            <CopyMetadataField
              caption={
                <ExpandableList limit={5}>{renderLyrics()}</ExpandableList>
              }
              depth={1}
            />
          )}
        </>
      )}

      {(selectable ||
        getVisibleMetadata('ProNumber') ||
        publishingChains.length > 0 ||
        recordingChains.length > 0) && (
        <>
          <CopyMetadataField
            caption="Rights"
            data={{
              metadata_keys: pick(metadata_keys, ['ProNumber']),
              rights_split_ids: pick(rights_split_ids, allRightsChainIds),
            }}
            depth={0}
            disabled={
              publishingChains.length === 0 &&
              recordingChains.length === 0 &&
              !getVisibleMetadata('ProNumber')
            }
            expanded={expandedSections.includes('Rights')}
            id="Rights"
            onExpand={toggleSection}
            onSelect={onSelect ? handleSelect : undefined}
          />
          {expandedSections.includes('Rights') && (
            <>
              {(selectable || getVisibleMetadata('ProNumber')) && (
                <CopyMetadataField
                  caption={renderMetadataField('ProNumber')}
                  data={{
                    metadata_keys: pick(metadata_keys, ['ProNumber']),
                  }}
                  depth={1}
                  disabled={!getVisibleMetadata('ProNumber')}
                  onSelect={onSelect ? handleSelect : undefined}
                />
              )}
              {renderRightsSubsection(
                'Publishing',
                publishingChains,
                publishingChainIds,
              )}
              {recordingTabEnabled &&
                renderRightsSubsection(
                  'Recording',
                  recordingChains,
                  recordingChainIds,
                )}
            </>
          )}
        </>
      )}

      {(selectable || tags.length > 0) && (
        <>
          <CopyMetadataField
            caption="Tags"
            data={{
              native_tags_indexes,
            }}
            depth={0}
            disabled={tags.length === 0}
            expanded={expandedSections.includes('Tags')}
            id="Tags"
            onExpand={toggleSection}
            onSelect={onSelect ? handleSelect : undefined}
          />
          {expandedSections.includes('Tags') &&
            tags.map((tag: NativeTag, index: number) => (
              <CopyMetadataField
                key={tag.id}
                caption={renderTag(tag)}
                data={{
                  native_tags_indexes: pick(native_tags_indexes, [
                    String(index),
                  ]),
                }}
                depth={1}
                onSelect={onSelect ? handleSelect : undefined}
              />
            ))}
        </>
      )}

      {(selectable || customFields.length > 0) && (
        <>
          <CopyMetadataField
            caption="Custom fields"
            data={{
              metadata_keys: pick(metadata_keys, [...customFields]),
            }}
            depth={0}
            disabled={visibleCustomFields.length === 0}
            expanded={expandedSections.includes('Custom fields')}
            id="Custom fields"
            onExpand={toggleSection}
            onSelect={onSelect ? handleSelect : undefined}
          />
          {expandedSections.includes('Custom fields') &&
            customFields.map((field: string) => (
              <CopyMetadataField
                key={field}
                caption={renderMetadataField(field)}
                data={{
                  metadata_keys: pick(metadata_keys, [field]),
                }}
                depth={1}
                disabled={
                  selectable
                    ? !track.metadata[field]
                    : !customFields.includes(field)
                }
                onSelect={onSelect ? handleSelect : undefined}
              />
            ))}
        </>
      )}

      {renderNotesSection()}
    </div>
  )

  const renderArtworkField = () => {
    return (
      <>
        Artwork:{' '}
        {selectable ? (
          <CopyMetadataArtwork artwork={track.artwork} />
        ) : (
          <>
            {!track.artwork && <CopyMetadataArtwork artwork={null} />}
            <CopyMetadataArtwork
              artwork={overrides?.artwork ?? track.artwork}
            />
          </>
        )}
      </>
    )
  }

  const renderMetadataField = (field: string) => {
    const getValue = (value: string | string[]): string =>
      Array.isArray(value) ? value.join(', ') : value

    const hasOverrides: boolean = Boolean(overrides?.metadata?.[field])
    return (
      <>
        {getTrackMetadataFieldName(field)}:{' '}
        {selectable || !hasOverrides ? (
          getValue(track.metadata[field])
        ) : (
          <>
            {track.metadata[field] && (
              <del>{getValue(track.metadata[field])}</del>
            )}{' '}
            {overrides?.metadata?.[field] && (
              <ins>{getValue(overrides?.metadata[field])}</ins>
            )}
          </>
        )}
      </>
    )
  }

  const renderLyrics = () => {
    if (!lyrics) return null
    const content = newlines(lyrics)
    return (
      <>
        {overrides?.metadata?.['Lyrics'] ? (
          <>
            {!track?.metadata['Lyrics'] && <del>blank</del>}
            <ins>{content}</ins>
          </>
        ) : (
          content
        )}
      </>
    )
  }

  const renderTag = (tag: NativeTag) => {
    return overrides?.native_tags &&
      !track?.native_tags?.some(({ id }) => tag.id === id) ? (
      <ins>{tag.name}</ins>
    ) : (
      tag.name
    )
  }

  return (
    <Draggable
      id={track.id}
      type={selectable ? COPY_METADATA_SOURCE : COPY_METADATA_TARGET}
    >
      <div
        className={cn(styles.copyMetadataTrack, {
          [styles.copyMetadataTrackDisabled]: disabled,
        })}
      >
        <HoverProvider>
          <Flex spacing="medium">
            <Flex.Content>
              <TrackInfo location="explorer" track={track} />
            </Flex.Content>
            <ButtonContainer>
              <Button
                appearance="transparent"
                caption="Remove"
                compact
                onClick={handleRemove}
              />
              {loading ? (
                <LoadingIndicator size="small" />
              ) : (
                <Button
                  appearance="icon"
                  compact
                  icon={isExpanded ? 'arrow-down' : 'arrow-right'}
                  onClick={handleExpand}
                />
              )}
            </ButtonContainer>
          </Flex>
        </HoverProvider>
        {!loading && isExpanded && renderMetadata()}
      </div>
    </Draggable>
  )
}

export default React.memo<Props>(CopyMetadataTrack)
